import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Download, FileText, Image, Film, File, ChevronDown, ChevronUp, Send, MessageSquare, Reply, RotateCcw } from "lucide-react";
import { ProfileAvatar } from "@/shared/components/ui/Avatar";
import { Spinner } from "@/shared/components/ui/Spinner";
import { formatDisplayName } from "@/shared/utils/formatters";
import { useFormatDate } from "@/shared/hooks/useFormatDate";
import apiClient from "@/shared/services/apiClient";
import {
  getSubtaskComments,
  getSubtaskAttachments,
  listSubtasks,
  addSubtaskComment,
  resetSubtaskCompletion,
} from "@/features/tasks/services/taskService";
import type {
  Subtask,
  TaskAssignee,
} from "@/features/tasks/types/task";
import type {
  SubtaskComment,
  SubtaskAttachment,
} from "@/features/tasks/services/taskService";

// ── File-naming helpers ───────────────────────────────────────────────────────

function getExtension(displayName: string): string {
  const parts = displayName.split(".");
  return parts.length > 1 ? parts.pop()!.toLowerCase() : "bin";
}

function buildFileName(
  firstName: string,
  createdAt: string,
  serial: number,
  displayName: string,
): string {
  const ext  = getExtension(displayName);
  const ts   = createdAt.replace(/[-:T\s]/g, "").replace(/\.\d+Z?$/, "").slice(0, 14);
  const name = (firstName || "User").replace(/[^a-zA-Z0-9]/g, "");
  return `${name}_${ts}_${serial}.${ext}`;
}

// ── Download helper ───────────────────────────────────────────────────────────

async function downloadBlob(attachmentId: string, fileName: string): Promise<void> {
  const response = await apiClient.get(`/attachments/proxy/${attachmentId}`, {
    responseType: "blob",
  });
  const url = URL.createObjectURL(response.data as Blob);
  const a   = document.createElement("a");
  a.href     = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── File-type icon ────────────────────────────────────────────────────────────

function FileIcon({ mimeType, className }: { mimeType: string; className?: string }) {
  if (mimeType.startsWith("image/")) return <Image className={className} />;
  if (mimeType.startsWith("video/")) return <Film className={className} />;
  if (mimeType === "application/pdf" || mimeType.startsWith("text/"))
    return <FileText className={className} />;
  return <File className={className} />;
}

// ── User-matching helpers ─────────────────────────────────────────────────────

/** Returns true if the comment belongs to this assignee.
 *  Tries user_id first; falls back to first+last name comparison. */
function commentBelongsTo(c: SubtaskComment, a: TaskAssignee): boolean {
  const cId = String(c.user_id ?? "");
  const aId = String(a.user_id ?? "");
  if (cId && aId && cId === aId) return true;
  return (
    c.first_name.toLowerCase().trim() === a.first_name.toLowerCase().trim() &&
    c.last_name.toLowerCase().trim()  === a.last_name.toLowerCase().trim()
  );
}

/** Returns true if the attachment was uploaded by this assignee.
 *  Tries uploader_user_id first; falls back to first+last name comparison. */
function attachmentBelongsTo(att: SubtaskAttachment, a: TaskAssignee): boolean {
  const uId = String(att.uploader_user_id ?? "");
  const aId = String(a.user_id ?? "");
  if (uId && aId && uId === aId) return true;
  return (
    att.uploader_first_name.toLowerCase().trim() === a.first_name.toLowerCase().trim() &&
    att.uploader_last_name.toLowerCase().trim()  === a.last_name.toLowerCase().trim()
  );
}

// ── Per-member response data ──────────────────────────────────────────────────

interface MemberRow {
  assignee: TaskAssignee;
  comments: SubtaskComment[];
  attachments: SubtaskAttachment[];
  completedByThisMember: boolean;
}

// ── Per-subtask data ──────────────────────────────────────────────────────────

interface SubtaskData {
  subtask: Subtask;
  memberRows: MemberRow[];
  comments: SubtaskComment[];
  loading: boolean;
}

interface CommentNode extends SubtaskComment {
  replies: CommentNode[];
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface TaskResponsesTabProps {
  taskId: string;
  isActive: boolean;
  onCountChange: (total: number) => void;
  onRefresh?: () => Promise<void> | void;
}

// ── Comment cell (expandable) ─────────────────────────────────────────────────

const CommentCell: React.FC<{ comments: SubtaskComment[] }> = ({ comments }) => {
  const [expanded, setExpanded] = useState(false);

  if (comments.length === 0) {
    return <span className="text-gray-300 text-xs">—</span>;
  }

  const latest = comments[comments.length - 1];
  const hasMore = comments.length > 1;

  return (
    <div className="space-y-1">
      {(expanded ? comments : [latest]).map((c) => (
        <p key={c.id} className="text-xs text-gray-700 whitespace-pre-wrap leading-snug line-clamp-3">
          {c.text}
        </p>
      ))}
      {hasMore && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-0.5 text-xs text-indigo-500 hover:text-indigo-700 transition-colors"
        >
          {expanded ? (
            <><ChevronUp className="w-3 h-3" /> less</>
          ) : (
            <><ChevronDown className="w-3 h-3" /> +{comments.length - 1} more</>
          )}
        </button>
      )}
    </div>
  );
};

// ── Attachment cell ───────────────────────────────────────────────────────────

const AttachmentCell: React.FC<{
  attachments: SubtaskAttachment[];
  globalSerial: number;
  downloadingId: string | null;
  onDownload: (att: SubtaskAttachment, serial: number) => void;
}> = ({ attachments, globalSerial, downloadingId, onDownload }) => {
  if (attachments.length === 0) {
    return <span className="text-gray-300 text-xs">—</span>;
  }

  return (
    <div className="flex flex-col gap-1">
      {attachments.map((a, i) => {
        const isImg        = a.file_type.startsWith("image/");
        const isDownloading = downloadingId === a.id;
        const serial       = globalSerial + i + 1;
        return (
          <div
            key={a.id}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border text-xs ${
              isImg ? "bg-blue-50 border-blue-100" : "bg-gray-50 border-gray-200"
            }`}
          >
            <FileIcon
              mimeType={a.file_type}
              className={`w-3.5 h-3.5 shrink-0 ${isImg ? "text-blue-500" : "text-gray-400"}`}
            />
            <span className="text-gray-700 truncate flex-1 max-w-[140px]">{a.display_name}</span>
            <button
              onClick={() => onDownload(a, serial)}
              disabled={isDownloading}
              className="shrink-0 text-gray-400 hover:text-indigo-600 transition-colors disabled:opacity-40"
              title="Download"
            >
              {isDownloading ? <Spinner size="sm" /> : <Download className="w-3 h-3" />}
            </button>
          </div>
        );
      })}
    </div>
  );
};

// ── Discussion tree ───────────────────────────────────────────────────────────

function buildCommentTree(comments: SubtaskComment[]): CommentNode[] {
  const nodes = new Map<string, CommentNode>();
  const roots: CommentNode[] = [];

  comments.forEach((comment) => {
    nodes.set(comment.id, { ...comment, replies: [] });
  });

  comments.forEach((comment) => {
    const node = nodes.get(comment.id);
    if (!node) return;

    const parentId = comment.parent_comment_id;
    const parent = parentId ? nodes.get(parentId) : null;
    if (parent) {
      parent.replies.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
}

const DiscussionComment: React.FC<{
  comment: CommentNode;
  depth?: number;
  onReply: (comment: SubtaskComment) => void;
  fmtDateTime: (value: string) => string;
  replyLabel: string;
}> = ({ comment, depth = 0, onReply, fmtDateTime, replyLabel }) => {
  const nested = depth > 0;

  return (
    <div className={nested ? "relative pl-4" : ""}>
      {nested && <div className="absolute left-0 top-0 bottom-0 w-px bg-gray-200" />}
      <div className={`flex gap-2.5 ${nested ? "pt-2" : ""}`}>
        <ProfileAvatar
          picId={comment.profile_pic_id}
          firstName={comment.first_name}
          lastName={comment.last_name}
          size="sm"
        />
        <div className="min-w-0 flex-1">
          <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm">
            <div className="flex items-baseline gap-2">
              <span className="truncate text-xs font-semibold text-gray-900">
                {formatDisplayName(comment.last_name, comment.first_name, comment.middle_name)}
              </span>
              <span className="shrink-0 text-[10px] text-gray-400">
                {fmtDateTime(comment.created_at)}
              </span>
            </div>
            <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-gray-700">
              {comment.text}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onReply(comment)}
            className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-gray-400 transition-colors hover:text-indigo-600"
          >
            <Reply className="h-3 w-3" />
            {replyLabel}
          </button>
          {comment.replies.length > 0 && (
            <div className="mt-1 space-y-1.5">
              {comment.replies.map((reply) => (
                <DiscussionComment
                  key={reply.id}
                  comment={reply}
                  depth={Math.min(depth + 1, 3)}
                  onReply={onReply}
                  fmtDateTime={fmtDateTime}
                  replyLabel={replyLabel}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

export const TaskResponsesTab: React.FC<TaskResponsesTabProps> = ({
  taskId,
  isActive,
  onCountChange,
  onRefresh,
}) => {
  const { t }                   = useTranslation();
  const { fmtDateTime }         = useFormatDate();
  const [rows, setRows]         = useState<SubtaskData[]>([]);
  const [fetched, setFetched]   = useState(false);
  const [downloadingAll, setDownloadingAll]     = useState(false);
  const [downloadingId, setDownloadingId]       = useState<string | null>(null);
  const [commentDrafts, setCommentDrafts]       = useState<Record<string, string>>({});
  const [replyTargets, setReplyTargets]         = useState<Record<string, SubtaskComment | null>>({});
  const [submittingCommentId, setSubmittingCommentId] = useState<string | null>(null);
  const [resettingCompletionKey, setResettingCompletionKey] = useState<string | null>(null);

  // ── Fetch all subtask responses lazily ────────────────────────────────────
  const fetchAll = useCallback(async (force = false) => {
    if (fetched && !force) return;
    setFetched(true);

    // Fetch subtasks fresh so we get row_id, assignees, completed_by populated
    const subtasks = await listSubtasks(taskId).catch(() => [] as Subtask[]);
    if (subtasks.length === 0) return;

    setRows(subtasks.map((st) => ({ subtask: st, memberRows: [], comments: [], loading: true })));

    const results = await Promise.allSettled(
      subtasks.map(async (st) => {
        const [comments, attachments] = await Promise.all([
          getSubtaskComments(st.row_id).catch(() => [] as SubtaskComment[]),
          getSubtaskAttachments(st.id).catch(() => [] as SubtaskAttachment[]),
        ]);

        const completedByIds = new Set(st.completed_by_ids ?? []);

        // Debug: log raw data so we can verify ID/name formats
        console.debug("[TaskResponses] subtask", st.id, {
          assignees:   st.assignees.map((a) => ({ id: a.user_id, name: `${a.first_name} ${a.last_name}` })),
          comments:    comments.map((c) => ({ id: c.user_id, name: `${c.first_name} ${c.last_name}`, text: c.text })),
          attachments: attachments.map((a) => ({ id: a.uploader_user_id, name: `${a.uploader_first_name} ${a.uploader_last_name}`, file: a.display_name })),
        });

        const memberRows: MemberRow[] = st.assignees.map((assignee) => ({
          assignee,
          comments:    comments.filter((c) => commentBelongsTo(c, assignee)),
          attachments: attachments.filter((a) => attachmentBelongsTo(a, assignee)),
          completedByThisMember: completedByIds.has(assignee.user_id),
        }));

        return { subtaskId: st.id, memberRows, comments };
      }),
    );

    setRows((prev) => {
      const next = prev.map((row) => {
        const match = results.find(
          (r) => r.status === "fulfilled" && r.value.subtaskId === row.subtask.id,
        );
        if (match && match.status === "fulfilled") {
          return {
            ...row,
            memberRows: match.value.memberRows,
            comments: match.value.comments,
            loading: false,
          };
        }
        return { ...row, loading: false };
      });
      return next;
    });
  }, [fetched, taskId]);

  // ── Derive count from rows and notify parent — outside the state updater
  //    to avoid React calling the side effect multiple times in concurrent mode.
  useEffect(() => {
    const total = rows.reduce(
      (sum, r) =>
        sum +
        r.memberRows.filter(
          (m) => m.comments.length > 0 || m.attachments.length > 0 || m.completedByThisMember,
        ).length,
      0,
    );
    onCountChange(total);
  }, [rows, onCountChange]);

  useEffect(() => {
    if (isActive) fetchAll();
  }, [isActive, fetchAll]);

  // ── Collect all attachments (for Download All) ────────────────────────────
  const allAttachments: SubtaskAttachment[] = rows.flatMap((row) =>
    row.memberRows.flatMap((m) => m.attachments),
  );
  const totalResponses = rows.reduce(
    (n, r) =>
      n +
      r.memberRows.filter(
        (m) => m.comments.length > 0 || m.attachments.length > 0 || m.completedByThisMember,
      ).length,
    0,
  );
  const totalMembers = rows.reduce((n, r) => n + r.memberRows.length, 0);

  // ── Download single ───────────────────────────────────────────────────────
  const handleDownloadSingle = async (att: SubtaskAttachment, serial: number) => {
    setDownloadingId(att.id);
    try {
      const fileName = buildFileName(att.uploader_first_name, att.created_at, serial, att.display_name);
      await downloadBlob(att.id, fileName);
    } catch (err) {
      console.error("Download failed", err);
    } finally {
      setDownloadingId(null);
    }
  };

  // ── Download All ──────────────────────────────────────────────────────────
  const handleDownloadAll = async () => {
    if (downloadingAll || allAttachments.length === 0) return;
    setDownloadingAll(true);
    try {
      for (let i = 0; i < allAttachments.length; i++) {
        const a = allAttachments[i];
        const fileName = buildFileName(a.uploader_first_name, a.created_at, i + 1, a.display_name);
        await downloadBlob(a.id, fileName);
        if (i < allAttachments.length - 1) {
          await new Promise((r) => setTimeout(r, 300));
        }
      }
    } catch (err) {
      console.error("Download All failed", err);
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleAddComment = async (row: SubtaskData) => {
    const text = (commentDrafts[row.subtask.id] ?? "").trim();
    if (!text || submittingCommentId) return;

    setSubmittingCommentId(row.subtask.id);
    try {
      await addSubtaskComment(row.subtask.row_id, text, replyTargets[row.subtask.id]?.id ?? null);
      setCommentDrafts((prev) => ({ ...prev, [row.subtask.id]: "" }));
      setReplyTargets((prev) => ({ ...prev, [row.subtask.id]: null }));
      await fetchAll(true);
      await onRefresh?.();
    } catch (err) {
      console.error("Failed to add subtask comment", err);
    } finally {
      setSubmittingCommentId(null);
    }
  };

  const handleResetCompletion = async (row: SubtaskData, assignee: TaskAssignee) => {
    const key = `${row.subtask.id}:${assignee.user_id}`;
    if (resettingCompletionKey) return;

    setResettingCompletionKey(key);
    try {
      await resetSubtaskCompletion(row.subtask.id, assignee.user_id);
      await fetchAll(true);
      await onRefresh?.();
    } catch (err) {
      console.error("Failed to reset subtask completion", err);
    } finally {
      setResettingCompletionKey(null);
    }
  };

  // ── Loading state ─────────────────────────────────────────────────────────
  if (!fetched) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0">

      {/* ── Summary bar ──────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-2 px-1 py-3 mb-1">
        <div className="flex items-center justify-between">
          {/* Overall % only shown when there are multiple subtasks to aggregate */}
          <div className="flex items-center gap-2">
            {rows.length > 1 && (
              <span className="text-xs font-semibold text-gray-700">
                {totalMembers > 0
                  ? Math.round((totalResponses / totalMembers) * 100)
                  : 0}%
              </span>
            )}
            <span className="text-xs text-gray-400">
              {totalResponses}/{totalMembers} responses
              {allAttachments.length > 0 && (
                <> · <span className="font-medium text-gray-600">{allAttachments.length}</span> {t("tasks.responses.files_label")}</>
              )}
            </span>
          </div>
          {allAttachments.length > 0 && (
            <button
              onClick={handleDownloadAll}
              disabled={downloadingAll}
              className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-50 transition-colors"
            >
              {downloadingAll ? <Spinner size="sm" /> : <Download className="w-3.5 h-3.5" />}
              {downloadingAll
                ? t("tasks.responses.downloading")
                : `${t("tasks.responses.download_all")} (${allAttachments.length})`}
            </button>
          )}
        </div>
        {/* Overall progress bar — only when aggregating multiple subtasks */}
        {rows.length > 1 && (
          <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full transition-all duration-500"
              style={{ width: `${totalMembers > 0 ? (totalResponses / totalMembers) * 100 : 0}%` }}
            />
          </div>
        )}
      </div>

      {/* ── Subtask tables ────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3">
        {rows.map((row) => {
          const doneIds    = new Set(row.subtask.completed_by_ids ?? []);
          const isComplete = row.subtask.assignees.length > 0 && doneIds.size >= row.subtask.assignees.length;
          const responseCount = row.memberRows.filter(
            (m) => m.comments.length > 0 || m.attachments.length > 0 || m.completedByThisMember,
          ).length;
          const discussion = buildCommentTree(row.comments);
          const replyTarget = replyTargets[row.subtask.id] ?? null;

          // Running serial offset for attachments within this subtask
          let attachSerial = 0;

          return (
            <div key={row.subtask.id} className="rounded-xl border border-gray-200 overflow-hidden">

              {/* Subtask header */}
              <div className={`px-3 py-2.5 ${isComplete ? "bg-emerald-50" : "bg-gray-50"}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isComplete ? "bg-emerald-500" : "bg-gray-400"}`} />
                    <p className="text-sm font-semibold text-gray-800 truncate">
                      {row.subtask.title}
                    </p>
                  </div>
                  <span className="text-xs text-gray-500 shrink-0 ml-2">
                    <span className={`font-semibold ${responseCount === row.memberRows.length ? "text-emerald-600" : "text-gray-700"}`}>
                      {row.memberRows.length > 0
                        ? Math.round((responseCount / row.memberRows.length) * 100)
                        : 0}%
                    </span>
                    {" "}
                    <span className="text-gray-400">({responseCount}/{row.memberRows.length})</span>
                  </span>
                </div>
                <div className="w-full h-1 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                    style={{ width: `${row.memberRows.length > 0 ? (responseCount / row.memberRows.length) * 100 : 0}%` }}
                  />
                </div>
              </div>

              <div className="border-b border-gray-100 bg-gray-50/60 px-3 py-3">
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                    <MessageSquare className="h-3.5 w-3.5 text-gray-400" />
                    {t("tasks.responses.discussion", { defaultValue: "Discussion" })}
                    {row.comments.length > 0 && (
                      <span className="rounded-full bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">
                        {row.comments.length}
                      </span>
                    )}
                  </div>
                </div>

                {discussion.length > 0 ? (
                  <div className="mb-3 space-y-3">
                    {discussion.map((comment) => (
                      <DiscussionComment
                        key={comment.id}
                        comment={comment}
                        onReply={(target) =>
                          setReplyTargets((prev) => ({ ...prev, [row.subtask.id]: target }))
                        }
                        fmtDateTime={fmtDateTime}
                        replyLabel={t("tasks.responses.reply", { defaultValue: "Reply" })}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="mb-3 rounded-lg border border-dashed border-gray-200 bg-white px-3 py-2 text-xs text-gray-400">
                    {t("tasks.responses.no_discussion", { defaultValue: "No discussion yet" })}
                  </p>
                )}

                {replyTarget && (
                  <div className="mb-2 flex items-center justify-between rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-1.5 text-xs">
                    <span className="min-w-0 truncate text-indigo-700">
                      {t("tasks.responses.replying_to", {
                        defaultValue: "Replying to {{name}}",
                        name: formatDisplayName(replyTarget.last_name, replyTarget.first_name, replyTarget.middle_name),
                      })}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setReplyTargets((prev) => ({ ...prev, [row.subtask.id]: null }))
                      }
                      className="ml-2 shrink-0 font-medium text-indigo-500 hover:text-indigo-700"
                    >
                      {t("tasks.responses.cancel_reply", { defaultValue: "Cancel" })}
                    </button>
                  </div>
                )}

                <form
                  className="flex gap-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    handleAddComment(row);
                  }}
                >
                  <textarea
                    value={commentDrafts[row.subtask.id] ?? ""}
                    onChange={(event) =>
                      setCommentDrafts((prev) => ({
                        ...prev,
                        [row.subtask.id]: event.target.value,
                      }))
                    }
                    placeholder={t("tasks.responses.add_subtask_comment", {
                      defaultValue: replyTarget ? "Write a reply..." : "Add a sub-task comment...",
                    })}
                    rows={1}
                    className="min-h-9 flex-1 resize-y rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                  />
                  <button
                    type="submit"
                    disabled={!(commentDrafts[row.subtask.id] ?? "").trim() || submittingCommentId === row.subtask.id}
                    className="self-end inline-flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-600 text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                    title={t("comments.add", { defaultValue: "Add comment" })}
                  >
                    {submittingCommentId === row.subtask.id ? <Spinner size="sm" /> : <Send className="h-4 w-4" />}
                  </button>
                </form>
              </div>

              {/* Member table */}
              {row.loading ? (
                <div className="flex items-center justify-center py-6">
                  <Spinner size="sm" />
                </div>
              ) : row.memberRows.length === 0 ? (
                <div className="px-4 py-4 text-xs text-gray-400 italic">
                  {t("tasks.responses.no_activity")}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-gray-100 bg-white">
                        <th className="text-left px-3 py-2 font-medium text-gray-500 w-[160px]">Member</th>
                        <th className="text-left px-3 py-2 font-medium text-gray-500">Comment</th>
                        <th className="text-left px-3 py-2 font-medium text-gray-500 w-[180px]">Attachment</th>
                        <th className="text-left px-3 py-2 font-medium text-gray-500 w-[90px]">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {row.memberRows.map((m) => {
                        const hasResponse = m.comments.length > 0 || m.attachments.length > 0;
                        const memberAttachSerial = attachSerial;
                        const resetKey = `${row.subtask.id}:${m.assignee.user_id}`;
                        attachSerial += m.attachments.length;

                        return (
                          <tr key={m.assignee.user_id} className="hover:bg-gray-50/50 transition-colors">

                            {/* Member */}
                            <td className="px-3 py-2.5 align-top">
                              <div className="flex items-center gap-2">
                                <ProfileAvatar
                                  picId={m.assignee.profile_pic_id}
                                  firstName={m.assignee.first_name}
                                  lastName={m.assignee.last_name}
                                  size="sm"
                                />
                                <div className="min-w-0">
                                  <p className="font-medium text-gray-800 truncate leading-tight">
                                    {formatDisplayName(
                                      m.assignee.last_name,
                                      m.assignee.first_name,
                                      m.assignee.middle_name,
                                    )}
                                  </p>
                                  {m.comments.length > 0 && (
                                    <p className="text-gray-400 text-[10px]">
                                      {fmtDateTime(m.comments[m.comments.length - 1].created_at)}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* Comment */}
                            <td className="px-3 py-2.5 align-top max-w-[200px]">
                              <CommentCell comments={m.comments} />
                            </td>

                            {/* Attachment */}
                            <td className="px-3 py-2.5 align-top">
                              <AttachmentCell
                                attachments={m.attachments}
                                globalSerial={memberAttachSerial}
                                downloadingId={downloadingId}
                                onDownload={handleDownloadSingle}
                              />
                            </td>

                            {/* Status */}
                            <td className="px-3 py-2.5 align-top">
                              {m.completedByThisMember ? (
                                <div className="flex items-center gap-1.5">
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                    {t("tasks.responses.completed", { defaultValue: "Completed" })}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => handleResetCompletion(row, m.assignee)}
                                    disabled={resettingCompletionKey === resetKey}
                                    className="inline-flex h-6 w-6 items-center justify-center rounded-md text-gray-300 transition-colors hover:bg-amber-50 hover:text-amber-500 disabled:opacity-40"
                                    title={t("tasks.subtasks.reset_completion", {
                                      defaultValue: "Reset completion",
                                    })}
                                  >
                                    {resettingCompletionKey === resetKey ? <Spinner size="sm" /> : <RotateCcw className="h-3.5 w-3.5" />}
                                  </button>
                                </div>
                              ) : hasResponse ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700">
                                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                  {t("tasks.responses.response_added", { defaultValue: "Response added" })}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500">
                                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                                  {t("tasks.responses.pending", { defaultValue: "Pending" })}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
